/**
 * File: FilterModal.js
 * Origin: isotect
 * Description: FilterModal component for configuring filter controls.
 *
 * Version: v1.0.0
 * Created: 2026-06-25
 * Last Updated: 2026-07-16
 *
 * Used By: isotect
 * Maintainer: Zachary Dicks
 *
 * Notes:
 * - Changes in this file may affect all consuming frontend modules.
 * - See Git repository 'isotect' for full change history.
 */

// Third-party packages
import { useEffect, useState } from "react";

// Local project files
import { TextButton } from "../ui/Buttons";
import DropDownField from "../../components/forms/DropDownField";

/**
 * Renders a modal that allows users to view and update filter values.
 *
 * @component
 * 
 * @param {Object} props - Component props
 * @param {Object} props.schema - Configuration schema describing the available filters
 * @param {Object} props.filters - Currently applied filter values
 * @param {(filters: Object) => void} props.setFilters - Updates the applied filter values
 * @param {() => void} props.onClose - Called when the modal should be closed
 *
 * @returns {JSX.Element} A filter modal
 */
function FilterModal({ schema, filters, setFilters, onClose }) {
    const [pendingFilters, setPendingFilters] = useState(filters);

    useEffect(() => {
        setPendingFilters(filters);
    }, [filters]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setFilters(pendingFilters);
        onClose();
    };

    const handleChange = (key, value) => {
        setPendingFilters((prev) => ({
            ...prev,
            [key]: value,
        }));
    };

    const handleReset = () => {
        setPendingFilters({});
    };

    return (
        <>
            <div className="filter-modal">
                <form className="filter-modal__form" onSubmit={handleSubmit}>
                    <fieldset className="filter-modal__fieldset">
                        {schema.filters.map((field) => {
                            if (field.type === "dropDownField") {
                                return (
                                    <DropDownField
                                        key={field.id}
                                        {...field}
                                        selection={
                                            pendingFilters[field.id] ?? ""
                                        }
                                        setSelection={(value) =>
                                            handleChange(field.id, value)
                                        }
                                        isRequired={false}
                                    />
                                );
                            }
                        })}
                    </fieldset>
                    <div className="filter-modal__text-buttons">
                        <TextButton {...schema.textButtons.apply} />
                        <TextButton
                            {...schema.textButtons.clear}
                            onClick={handleReset}
                        />
                    </div>
                </form>
            </div>
        </>
    );
}

export default FilterModal;
